import { Title } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import pluralize from "pluralize";

import { useFetchSubstage } from "../../ClipboardScore/hooks/useFetchSubstage";
import { Button } from "../../components/Button";
import { TextButton } from "../../components/TextButton";
import { InAppAdCard } from "../../Shift/InAppAd/Card";
import { type InferedInAppAdAttributesSchema } from "../../Shift/Open/types";
import { useShiftDiscoveryUserFiltersContext } from "../Filters/useUserFiltersContext";
import { SHIFT_DISCOVERY_CALENDAR_PATH, SHIFT_DISCOVERY_FILTERS_PATH } from "../paths";
import { useShiftModalsDataContext } from "../useShiftModalsDataContext";
import { ListViewBanners } from "./Banners";
import { WorkerReferralCta } from "./WorkerReferralCta";
import { WorkplaceReferralCta } from "./WorkplaceReferralCta";

interface ListViewPageOpenShiftsEmptyStateProps {
  readonly daysCount: number;
  readonly inAppAd?: InferedInAppAdAttributesSchema;
  readonly children?: React.ReactNode;
  readonly onClearFilters: () => void;
  readonly unfilteredShiftsLength: number;
}

export function ListViewPageOpenShiftsEmptyState(props: ListViewPageOpenShiftsEmptyStateProps) {
  const { daysCount, inAppAd, children, onClearFilters, unfilteredShiftsLength } = props;
  const { navigateToModal } = useShiftModalsDataContext();
  const { appliedFiltersCount } = useShiftDiscoveryUserFiltersContext();

  const { data: substage } = useFetchSubstage();
  const leadTimeThresholdInHrs = substage?.accessMetadata?.leadTimeThresholdInHrs;

  const isFiltersRedesignEnabled = useCbhFlag(CbhFeatureFlag.SHIFT_DISCOVERY_FILTERS_REDESIGN, {
    defaultValue: false,
  });

  let updatedDaysCount = daysCount;
  let showLoadMoreDates = true;

  if (leadTimeThresholdInHrs) {
    const leadTimeInDays = Math.floor(leadTimeThresholdInHrs / 24);
    if (leadTimeInDays < daysCount) {
      updatedDaysCount = leadTimeInDays;

      if (substage.nonTargetedQualifications.length === 0) {
        showLoadMoreDates = false;
      }
    }
  }

  // Needed so we can scroll past the floating button to see the content at bottom
  const SAFE_SPACE_BOTTOM = 10;

  return (
    <Box sx={{ flex: "1", overflow: "auto", pb: SAFE_SPACE_BOTTOM }}>
      <ListViewBanners />
      <Stack sx={{ flex: "1", p: 5, pt: 10 }} justifyContent="center" spacing={5}>
        <Title semibold variant="h4" component="h3" sx={{ textAlign: "center" }}>
          {appliedFiltersCount > 0
            ? `There are no open shifts matching your filters. Check back later!`
            : `There are no open shifts in the next ${updatedDaysCount} ${pluralize(
                "day",
                updatedDaysCount
              )}`}
        </Title>

        {appliedFiltersCount === 0 ? (
          showLoadMoreDates && (
            <TextButton
              sx={{ alignSelf: "center" }}
              onClick={() => {
                logEvent(APP_V2_USER_EVENTS.OPEN_SHIFTS_CALENDAR_OPENED);
                if (isFiltersRedesignEnabled) {
                  navigateToModal(SHIFT_DISCOVERY_FILTERS_PATH);
                } else {
                  navigateToModal(SHIFT_DISCOVERY_CALENDAR_PATH);
                }
              }}
            >
              Load more dates
            </TextButton>
          )
        ) : (
          <Button
            variant="contained"
            size="medium"
            sx={{ width: "fit-content", alignSelf: "center" }}
            onClick={onClearFilters}
          >
            Reset filters to see all {unfilteredShiftsLength} shifts
          </Button>
        )}

        <Stack spacing={3} sx={{ pt: 8 }}>
          <WorkerReferralCta />
          <WorkplaceReferralCta />
          {inAppAd && <InAppAdCard inAppAd={inAppAd} sx={{ mx: 5 }} />}
        </Stack>
        {children}
      </Stack>
    </Box>
  );
}
